import IMessage from "../interface/IMessage";
import getFileName from "./getFileName";
import getFileSize, { getFormattedFileSize } from "./getFileSize";
import { getTheme } from "../utils/theme/getTheme";
import type { Brand } from "../lib/ChatbotConfig";

const urlRegex: RegExp = /(https?:\/\/[^\s]+)/g;

interface IMessagePart {
  type: "text" | "link";
  value: string;
}

interface ITextRun {
  type: "text" | "link" | "newline";
  value: string;
}

export async function downloadChat(messages: IMessage[], brand: Brand = "training") {
  const jsPDF = (await import("jspdf")).default;
  const pdfTheme = {
    primaryMain: [60, 60, 125],
    primaryLight: [0, 113, 178],
    secondary: [54, 54, 87],
    text: [48, 48, 48],
    textMuted: [99, 111, 129],
    surface: [255, 255, 255],
    surfaceMuted: [246, 249, 252],
    border: [220, 228, 236],
    userBubble: [204, 227, 240],
    botBubble: [234, 234, 234],
    systemBubble: [239, 240, 248],
  } as const;
  const theme = getTheme(brand);

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 16;
  const contentWidth = pageWidth - margin * 2;
  const cardPadding = 4;
  const cardGap = 5;
  const footerHeight = 10;
  const headerHeight = 24;
  let y = margin;

  const formatExportDate = (date: Date) =>
    new Intl.DateTimeFormat("de-DE", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);

  const hexToRgb = (hexColor: string): [number, number, number] => {
    const normalized = hexColor.replace("#", "").trim();
    const expanded =
      normalized.length === 3
        ? normalized
            .split("")
            .map((char) => `${char}${char}`)
            .join("")
        : normalized;

    if (expanded.length !== 6) {
      return [...pdfTheme.primaryMain];
    }

    const red = Number.parseInt(expanded.slice(0, 2), 16);
    const green = Number.parseInt(expanded.slice(2, 4), 16);
    const blue = Number.parseInt(expanded.slice(4, 6), 16);

    if ([red, green, blue].some((value) => Number.isNaN(value))) {
      return [...pdfTheme.primaryMain];
    }

    return [red, green, blue];
  };

  const splitMessageParts = (messageText: string): IMessagePart[] => {
    const parts: IMessagePart[] = [];
    let lastIndex = 0;
    const linkRegex = new RegExp(urlRegex);
    let matchResult = linkRegex.exec(messageText);

    while (matchResult !== null) {
      const match = matchResult[0];
      const index = matchResult.index;

      if (lastIndex < index) {
        parts.push({
          type: "text",
          value: messageText.slice(lastIndex, index),
        });
      }

      parts.push({
        type: "link",
        value: match,
      });

      lastIndex = index + match.length;
      matchResult = linkRegex.exec(messageText);
    }

    if (lastIndex < messageText.length) {
      parts.push({
        type: "text",
        value: messageText.slice(lastIndex),
      });
    }

    return parts;
  };

  const createTextRuns = (messageText: string): ITextRun[] =>
    splitMessageParts(messageText).flatMap((part: IMessagePart): ITextRun[] => {
      if (part.type === "link") {
        return [{ type: "link", value: part.value }];
      }

      return part.value
        .split(/(\n|\s+)/)
        .filter((value: string) => value !== "")
        .map((value: string): ITextRun => {
          if (value === "\n") {
            return { type: "newline", value };
          }

          return { type: "text", value };
        });
    });

  const getLinkColor = (message: IMessage): [number, number, number] =>
    hexToRgb(
      message.sender === "user"
        ? theme.palette.widgetStyles.userChatBubble.linkColor
        : theme.palette.widgetStyles.botChatBubble.linkColor,
    );

  const renderRichText = ({
    runs,
    startX,
    startY,
    maxWidth,
    lineHeight,
    baseHeight,
    textColor,
    linkColor,
    shouldRender,
  }: {
    runs: ITextRun[];
    startX: number;
    startY: number;
    maxWidth: number;
    lineHeight: number;
    baseHeight: number;
    textColor: readonly number[];
    linkColor: readonly number[];
    shouldRender: boolean;
  }): number => {
    let currentX = startX;
    let currentY = startY;
    let lineCount = 1;

    const moveToNextLine = () => {
      currentX = startX;
      currentY += lineHeight;
      lineCount += 1;
    };

    runs.forEach((run: ITextRun) => {
      if (run.type === "newline") {
        moveToNextLine();
        return;
      }

      const segmentWidth = doc.getTextWidth(run.value);
      const isWhitespace = /^\s+$/.test(run.value);

      if (currentX > startX && currentX + segmentWidth > startX + maxWidth) {
        moveToNextLine();
      }

      if (isWhitespace && currentX === startX) {
        return;
      }

      if (shouldRender && !isWhitespace) {
        const activeColor = run.type === "link" ? linkColor : textColor;
        doc.setTextColor(activeColor[0], activeColor[1], activeColor[2]);
        doc.text(run.value, currentX, currentY);

        if (run.type === "link") {
          const underlineY = currentY + 0.45;
          doc.setDrawColor(linkColor[0], linkColor[1], linkColor[2]);
          doc.setLineWidth(0.2);
          doc.line(currentX, underlineY, currentX + segmentWidth, underlineY);
          doc.link(
            currentX,
            currentY - baseHeight,
            segmentWidth,
            baseHeight + 1,
            {
              url: run.value,
            },
          );
        }
      }

      currentX += segmentWidth;
    });

    return Math.max(baseHeight, baseHeight + (lineCount - 1) * lineHeight);
  };

  const formatMessageDate = (timeStamp: string | null) => {
    if (!timeStamp) return "";

    const parsedDate = new Date(timeStamp);
    if (Number.isNaN(parsedDate.getTime())) return timeStamp;

    return formatExportDate(parsedDate);
  };

  const getSenderLabel = (message: IMessage) => {
    switch (message.sender) {
      case "user":
        return "Du";
      case "agent":
        return message.agent.name?.trim() || "Beratung";
      case "bot":
        return "Mia";
      default:
        return "System";
    }
  };

  const getSenderPalette = (message: IMessage) => {
    switch (message.sender) {
      case "user":
        return {
          fill: pdfTheme.userBubble,
          title: pdfTheme.primaryMain,
          meta: pdfTheme.textMuted,
          text: pdfTheme.text,
        };
      case "agent":
      case "bot":
        return {
          fill: pdfTheme.botBubble,
          title: pdfTheme.primaryMain,
          meta: pdfTheme.textMuted,
          text: pdfTheme.text,
        };
      default:
        return {
          fill: pdfTheme.systemBubble,
          title: pdfTheme.secondary,
          meta: pdfTheme.textMuted,
          text: pdfTheme.text,
        };
    }
  };

  const getBubbleCaption = (message: IMessage, senderLabel: string) => {
    const messageDate = formatMessageDate(message.timeStamp);

    if (!messageDate) return senderLabel;

    return `${messageDate} | ${senderLabel}`;
  };

  const ensureSpace = (requiredHeight: number) => {
    const limit = pageHeight - margin - footerHeight;

    if (y + requiredHeight <= limit) return;

    doc.addPage();
    y = margin;
  };

  const renderPageHeader = () => {
    doc.setFillColor(
      pdfTheme.primaryLight[0],
      pdfTheme.primaryLight[1],
      pdfTheme.primaryLight[2],
    );
    doc.roundedRect(margin, y, contentWidth, headerHeight, 4, 4, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(255, 255, 255);
    doc.text("Chat-Verlauf", margin + 6, y + 9);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(230, 242, 250);
    doc.text(
      `Exportiert am ${formatExportDate(new Date())}`,
      margin + 6,
      y + 15,
    );

    y += headerHeight + 8;
  };

  const renderMessageCard = (message: IMessage) => {
    const senderLabel = getSenderLabel(message);
    const palette = getSenderPalette(message);
    const isUserMessage = message.sender === "user";
    const isSystemMessage = message.sender === "system";
    const messageFontSize = 9.5;
    const attachmentFontSize = 8.5;
    const textTopOffset = 3.8;
    const bubbleWidth = isSystemMessage
      ? contentWidth * 0.82
      : contentWidth * 0.78;
    const bubbleX = isSystemMessage
      ? margin + (contentWidth - bubbleWidth) / 2
      : isUserMessage
        ? pageWidth - margin - bubbleWidth
        : margin;
    const textX = bubbleX + cardPadding + 2;
    const textWidth = bubbleWidth - cardPadding * 2 - 2;
    const textContent = message.text?.trim() || "Keine Textnachricht";
    const textRuns = createTextRuns(textContent);
    const attachmentLabel = message.content
      ? `Anhang: ${getFileName(message.content)} (${getFormattedFileSize(
          getFileSize(message.content),
        )})`
      : null;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(messageFontSize);
    const baseTextHeight = Math.max(doc.getTextDimensions("Ag").h, 4);
    const textLineHeight = Math.max(baseTextHeight * 1.15, 4.2);
    const textHeight = renderRichText({
      runs: textRuns,
      startX: textX,
      startY: y + cardPadding + textTopOffset,
      maxWidth: textWidth,
      lineHeight: textLineHeight,
      baseHeight: baseTextHeight,
      textColor: palette.text,
      linkColor: getLinkColor(message),
      shouldRender: false,
    });
    const totalTextHeight = Math.max(textHeight, 4);

    doc.setFont("helvetica", "italic");
    doc.setFontSize(attachmentFontSize);
    const attachmentLines = attachmentLabel
      ? doc.splitTextToSize(attachmentLabel, textWidth)
      : [];
    const attachmentHeight =
      attachmentLines.length > 0
        ? doc.getTextDimensions(attachmentLines, { maxWidth: textWidth }).h +
          1.5
        : 0;
    const bubbleHeight =
      textTopOffset + totalTextHeight + attachmentHeight + cardPadding + 1.5;
    const captionGap = 2.5;
    const captionHeight = 3.5;
    const cardHeight = bubbleHeight + captionGap + captionHeight;
    const captionText = getBubbleCaption(message, senderLabel);
    const captionAlign = isSystemMessage
      ? "center"
      : isUserMessage
        ? "right"
        : "left";
    const captionInset = 1;
    const captionX = isSystemMessage
      ? bubbleX + bubbleWidth / 2
      : isUserMessage
        ? bubbleX + bubbleWidth - captionInset
        : bubbleX + captionInset;

    ensureSpace(cardHeight + cardGap);

    doc.setFillColor(palette.fill[0], palette.fill[1], palette.fill[2]);
    doc.roundedRect(bubbleX, y, bubbleWidth, bubbleHeight, 4, 4, "F");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(messageFontSize);
    renderRichText({
      runs: textRuns,
      startX: textX,
      startY: y + cardPadding + textTopOffset,
      maxWidth: textWidth,
      lineHeight: textLineHeight,
      baseHeight: baseTextHeight,
      textColor: palette.text,
      linkColor: getLinkColor(message),
      shouldRender: true,
    });

    if (attachmentLines.length > 0) {
      doc.setFont("helvetica", "italic");
      doc.setFontSize(attachmentFontSize);
      doc.setTextColor(palette.meta[0], palette.meta[1], palette.meta[2]);
      doc.text(
        attachmentLines,
        textX,
        y + cardPadding + textTopOffset + totalTextHeight + 1.5,
        { lineHeightFactor: 1.1 },
      );
    }

    const captionY = y + bubbleHeight + captionGap + captionHeight;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(palette.meta[0], palette.meta[1], palette.meta[2]);
    doc.text(captionText, captionX, captionY, {
      align: captionAlign,
    });

    y += cardHeight + cardGap;
  };

  renderPageHeader();

  messages.forEach((message: IMessage) => {
    renderMessageCard(message);
  });

  const pageCount = doc.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(
      pdfTheme.textMuted[0],
      pdfTheme.textMuted[1],
      pdfTheme.textMuted[2],
    );
    doc.text(
      `Seite ${page} von ${pageCount}`,
      pageWidth - margin,
      pageHeight - 4,
      { align: "right" },
    );
  }

  doc.save("chat-history.pdf");
}
